import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton, makeStyles } from '@material-ui/core'
import { useFieldArray } from 'react-hook-form'
import SydButton from '../../../commonDesign/Button'
import Icon from '../../../atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import DynamicFieldRenderer from './DynamicFieldRenderer'
import { sortByOrdinal } from './utils/fieldUtils'

const useStyles = makeStyles(() => ({
  groupContainer: {
    marginBottom: 'var(--margin-lg)',
    '& input, & textarea, & select': {
      boxSizing: 'border-box'
    }
  },
  groupTitle: {
    fontSize: 'var(--text-size-lg)',
    fontWeight: 'var(--text-weight-bold)',
    marginBottom: 'var(--margin-md)',
    color: 'var(--color-primary-700)'
  },
  groupDescription: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-600)',
    marginBottom: 'var(--margin-md)'
  },
  repeaterItem: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '6px',
    padding: '12px 14px',
    marginBottom: '8px',
    backgroundColor: 'var(--color-neutral-050, transparent)',
    '&:last-of-type': {
      marginBottom: 0
    }
  },
  repeaterItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  repeaterItemLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  repeaterAdd: {
    marginTop: '10px'
  },
  repeaterEmpty: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-500)',
    padding: 'var(--padding-md) 0'
  },
  repeaterError: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-danger)',
    marginTop: 'var(--margin-xs)'
  }
}))

function buildEmptyRow (fields) {
  const row = {}
  const normalized = Array.isArray(fields) ? fields : Object.values(fields || {})
  normalized.forEach(f => {
    switch (f.editorType) {
      case 'bool': row[f.id] = false; break
      case 'number': row[f.id] = null; break
      case 'select': row[f.id] = f.multiple ? [] : null; break
      default: row[f.id] = ''; break
    }
  })
  return row
}

const groupPropTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    ordinal: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    repeatable: PropTypes.bool,
    addLabel: PropTypes.string,
    itemLabel: PropTypes.string,
    minItems: PropTypes.number,
    maxItems: PropTypes.number,
    fields: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object])
  }).isRequired,
  control: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  initializationData: PropTypes.any
}

function RepeatableFieldGroup ({ group, control, form, initializationData }) {
  const classes = useStyles()

  const rawFields = group.fields || []
  const normalizedFields = Array.isArray(rawFields)
    ? rawFields
    : Object.values(rawFields)
  const sortedFields = useMemo(
    () => sortByOrdinal(normalizedFields),
    [normalizedFields]
  )

  const { fields, append, remove } = useFieldArray({
    name: group.id,
    control
  })

  const atMax = group.maxItems != null && fields.length >= group.maxItems
  const atMin = group.minItems != null && fields.length <= group.minItems
  const arrayError = form.formState.errors?.[group.id]?.root?.message ||
    form.formState.errors?.[group.id]?.message

  return (
    <div className={classes.groupContainer}>
      {group.title && (
        <div className={classes.groupTitle}>{group.title}</div>
      )}
      {group.description && (
        <div className={classes.groupDescription}>{group.description}</div>
      )}

      {fields.length === 0 && (
        <div className={classes.repeaterEmpty}>No items yet.</div>
      )}

      {fields.map((item, index) => (
        <div key={item.id} className={classes.repeaterItem}>
          <div className={classes.repeaterItemHeader}>
            <span className={classes.repeaterItemLabel}>
              {group.itemLabel || 'Item'} {index + 1}
            </span>
            <IconButton
              size='small'
              onClick={() => remove(index)}
              disabled={atMin}
            >
              <Icon name={ICON_NAMES.removeCircle} customSize='18px' />
            </IconButton>
          </div>
          <Grid container spacing={2}>
            {sortedFields.map(subField => (
              <DynamicFieldRenderer
                key={subField.id}
                field={{
                  ...subField,
                  id: `${group.id}.${index}.${subField.id}`
                }}
                control={control}
                form={form}
                initializationData={initializationData}
              />
            ))}
          </Grid>
        </div>
      ))}

      {arrayError && (
        <div className={classes.repeaterError}>{arrayError}</div>
      )}

      <div className={classes.repeaterAdd}>
        <SydButton
          variant='outline'
          size='sm'
          icon={ICON_NAMES.add}
          onClick={() => append(buildEmptyRow(normalizedFields))}
          disabled={atMax}
        >
          {group.addLabel || 'Add Item'}
        </SydButton>
      </div>
    </div>
  )
}

RepeatableFieldGroup.propTypes = groupPropTypes

function StaticFieldGroup ({ group, control, form, initializationData }) {
  const classes = useStyles()

  const rawFields = group.fields || []
  const normalizedFields = Array.isArray(rawFields)
    ? rawFields
    : Object.values(rawFields)
  const sortedFields = sortByOrdinal(normalizedFields)

  return (
    <div className={classes.groupContainer}>
      {group.title && (
        <div className={classes.groupTitle}>{group.title}</div>
      )}
      {group.description && (
        <div className={classes.groupDescription}>{group.description}</div>
      )}
      <Grid container spacing={2}>
        {sortedFields.map(field => (
          <DynamicFieldRenderer
            key={field.id}
            field={field}
            control={control}
            form={form}
            initializationData={initializationData}
          />
        ))}
      </Grid>
    </div>
  )
}

StaticFieldGroup.propTypes = groupPropTypes

function FieldGroup (props) {
  if (props.group.repeatable) {
    return <RepeatableFieldGroup {...props} />
  }
  return <StaticFieldGroup {...props} />
}

FieldGroup.propTypes = groupPropTypes

export default FieldGroup
